import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks/useLocalize';
import * as graphql from '@treams/treams-network';
import { Combo } from '@treams/treams-ui/common/Combo';
import { Modal } from '@treams/treams-ui/common/Modal';
import { Label } from '@treams/treams-ui/common/Text';
import { Input } from '@treams/treams-ui/form/controls/Input';

type JobPositionTitleFieldProps = {
	translationKey: 'jobPositions' | 'roles';
	value: { custom: string } | { value: string } | undefined;
	onChange: (value: { custom: string } | { value: string } | undefined) => void;
};

function JobPositionTitleField({ translationKey, value, onChange }: JobPositionTitleFieldProps) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [fetchJobTitles, jobTitles] = graphql.useJobTitlesLazyQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	return (
		<Flex alignItems="center">
			<Box flexGrow={1} flexShrink={1} flexBasis="40%">
				<Label>{t(`settings.employees.${translationKey}.create.labels.title`)}:</Label>
			</Box>
			<Box flexGrow={1} flexShrink={1} flexBasis="60%">
				<Combo
					autoFocus
					fetch={fetchJobTitles}
					choice={value}
					onChange={onChange}
					options={
						jobTitles.data &&
						_.sortBy(
							jobTitles.data.jobTitles.edges.map(({ node }) => ({
								title: localize(node.title).text,
								value: node.id,
							})),
							'title',
						)
					}
					explanation={t(`settings.employees.${translationKey}.create.custom.title`)}
				/>
			</Box>
		</Flex>
	);
}

type JobPositionLevelFieldProps = {
	translationKey: 'jobPositions' | 'roles';
	value: { custom: string } | { value: string } | undefined;
	onChange: (value: { custom: string } | { value: string } | undefined) => void;
};

function JobPositionLevelField({ translationKey, value, onChange }: JobPositionLevelFieldProps) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [fetchJobLevels, jobLevels] = graphql.useJobLevelsLazyQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	return (
		<Flex mt={2} alignItems="center">
			<Box flexGrow={1} flexShrink={1} flexBasis="40%">
				<Label>{t(`settings.employees.${translationKey}.create.labels.level`)}:</Label>
			</Box>
			<Box flexGrow={1} flexShrink={1} flexBasis="60%">
				<Combo
					fetch={fetchJobLevels}
					choice={value}
					onChange={onChange}
					placeholder={t(`settings.employees.${translationKey}.create.level.placeholder`)}
					options={
						jobLevels.data &&
						_.sortBy(
							jobLevels.data.jobLevels.edges.map(({ node }) => ({
								title: localize(node.title).text,
								value: node.id,
							})),
							'title',
						)
					}
					explanation={t(`settings.employees.${translationKey}.create.custom.level`)}
				/>
			</Box>
		</Flex>
	);
}

type JobPositionStartFieldProps = {
	translationKey: 'jobPositions' | 'roles';
	value: Date;
	onChange: (value: Date) => void;
};

function JobPositionStartField({ translationKey, value, onChange }: JobPositionStartFieldProps) {
	const { t } = useTranslation();

	return (
		<Flex mt={2} alignItems="center">
			<Box flexGrow={1} flexShrink={1} flexBasis="40%">
				<Label>{t(`settings.employees.${translationKey}.create.labels.startDate`)}:</Label>
			</Box>
			<Box flexGrow={1} flexShrink={1} flexBasis="60%">
				<Input.DatePicker value={value} onChange={(value) => onChange(value ?? new Date())} />
			</Box>
		</Flex>
	);
}

type CreateJobPositionProps = {
	user: graphql.User;
	hideJobLevel?: boolean;
	translationKey?: 'jobPositions' | 'roles';
	onCancel: () => void;
	onDismiss: () => void;
	onCreate: () => Promise<void>;
};

export default function CreateJobPosition({
	user,
	hideJobLevel,
	translationKey = 'jobPositions',
	onCancel,
	onDismiss,
	onCreate,
}: CreateJobPositionProps) {
	const { t } = useTranslation();

	const { language } = useLocalize();

	const [create] = graphql.useCreateJobPositionMutation();
	const [createJobTitle] = graphql.useCreateJobTitleMutation();
	const [createJobLevel] = graphql.useCreateJobLevelMutation();

	const [jobTitle, setJobTitle] = useState<{ custom: string } | { value: string } | undefined>(
		undefined,
	);

	const [jobLevel, setJobLevel] = useState<{ custom: string } | { value: string } | undefined>(
		undefined,
	);

	const [startDate, setStartDate] = useState(new Date());

	async function createJobTitleIfNecessary(jobTitle: { custom: string } | { value: string }) {
		if ('value' in jobTitle) {
			return jobTitle.value;
		}

		const response = await createJobTitle({
			variables: { input: { title: [{ languageID: language.id, text: jobTitle.custom }] } },
		});

		return response.data!.createJobTitle.jobTitle.id;
	}

	async function createJobLevelIfNecessary(jobLevel: { custom: string } | { value: string }) {
		if ('value' in jobLevel) {
			return jobLevel.value;
		}

		const response = await createJobLevel({
			variables: { input: { title: [{ languageID: language.id, text: jobLevel.custom }] } },
		});

		return response.data!.createJobLevel.jobLevel.id;
	}

	return (
		<Modal width={400} height="auto">
			<Modal.Header title={t(`settings.employees.${translationKey}.create.title`)} />
			<Modal.Contents>
				<JobPositionTitleField
					translationKey={translationKey}
					value={jobTitle}
					onChange={setJobTitle}
				/>
				{jobTitle && !hideJobLevel && (
					<JobPositionLevelField
						translationKey={translationKey}
						value={jobLevel}
						onChange={setJobLevel}
					/>
				)}
				{jobTitle && (
					<JobPositionStartField
						translationKey={translationKey}
						value={startDate}
						onChange={setStartDate}
					/>
				)}
			</Modal.Contents>
			<Modal.FormFooter
				disabled={!jobTitle}
				onCancel={onCancel}
				onSave={async () => {
					const jobTitleID = await createJobTitleIfNecessary(jobTitle!);
					const jobLevelID = jobLevel && (await createJobLevelIfNecessary(jobLevel!));

					await create({
						variables: {
							input: {
								jobTitleID,
								jobLevelID,
								startDate,
								userID: user.id,
							},
						},
					});

					await onCreate();
					onDismiss();
				}}
			/>
		</Modal>
	);
}
